/* Digi Merchant original illustrative geometry. Three.js r180/MIT; no branded or licensed model asset. */
const instances = new WeakMap();
const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const mix = (a, b, t) => a + (b - a) * t;

async function mount(root) {
  if (!root || instances.has(root)) return;
  const media = matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = matchMedia('(max-width: 749px)');
  const status = root.querySelector('[data-scene-state]');
  const pause = root.querySelector('[data-scene-pause]');
  const viewport = root.querySelector('[data-scene-viewport]');
  const controller = new AbortController();
  let renderer, scene, camera, observer, resizeObserver, frame = 0, alive = true, visible = false, paused = false, current = 0, target = 0;
  let geometryWorld, phone, tablet, laptop, orbit, environmentTarget, lights = [], textures = [];
  const state = { destroy };
  instances.set(root, state);
  function fallback(reason) {
    root.dataset.webgl = 'fallback';
    if (status) status.textContent = reason || 'Real product photography';
    if (pause) pause.hidden = true;
  }
  function eligible() { return alive && !media.matches && !mobile.matches && !navigator.connection?.saveData && root.dataset.sceneEnabled !== 'false'; }
  function destroy() {
    if (!alive) return;
    alive = false;
    controller.abort();
    cancelAnimationFrame(frame);
    observer?.disconnect(); resizeObserver?.disconnect();
    if (scene) scene.traverse(object => { object.geometry?.dispose(); if (object.material) { const mats = Array.isArray(object.material) ? object.material : [object.material]; mats.forEach(mat => mat.dispose()); } });
    textures.forEach(texture => texture.dispose()); environmentTarget?.dispose();
    renderer?.dispose(); renderer?.domElement.remove();
    instances.delete(root);
  }
  function progress() {
    const rect = root.querySelector('[data-scene-story]').getBoundingClientRect();
    const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 140;
    const storyStyle = getComputedStyle(root.querySelector('[data-scene-story]'));
    const paddingTop = parseFloat(storyStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(storyStyle.paddingBottom) || 0;
    const worldHeight = root.querySelector('.du-world').getBoundingClientRect().height;
    const runway = Math.max(1, rect.height - paddingTop - paddingBottom - worldHeight);
    return clamp((header + 22 - rect.top - paddingTop) / runway);
  }
  // Each device turns around its own centre, in a separate screen-space lane.
  function poseDevices(p) {
    const orbitStops = [[0,-.32],[.18,Math.PI/2-.32],[.43,Math.PI-.32],[.68,Math.PI*1.5-.32],[.88,Math.PI*2-.32],[1,Math.PI*2-.32]];
    let angle = -.32;
    for (let index=1; index<orbitStops.length; index++) {
      if(p<=orbitStops[index][0]) {const a=orbitStops[index-1],b=orbitStops[index],t=clamp((p-a[0])/(b[0]-a[0])),smooth=t*t*(3-2*t);angle=mix(a[1],b[1],smooth);break;}
    }
    geometryWorld.rotation.set(0,0,0);
    phone.position.set(-1.22,1.0,0);phone.scale.setScalar(.8);phone.rotation.set(.025,angle,-.04);
    tablet.position.set(1.15,1.06,-.08);tablet.scale.setScalar(.8);tablet.rotation.set(.025,angle,.04);
    laptop.position.set(0,-1.66,.12);laptop.scale.setScalar(.86);laptop.rotation.set(0,angle,0);
    laptop.userData.lid.rotation.x = -.18;
    orbit.rotation.z = 0;
    return angle;
  }
  function draw() {
    frame = 0;
    if (!alive || !visible || paused || document.hidden || !eligible() || !renderer) return;
    target = progress();
    current = Math.abs(target - current) < .002 ? target : mix(current, target, .13);
    const p = current;
    const angle = poseDevices(p);
    root.dataset.sceneAngle = (angle*180/Math.PI).toFixed(1);
    root.style.setProperty('--scene-progress', p.toFixed(4));
    const active = Math.min(2, Math.floor(p * 3));
    root.querySelectorAll('[data-scene-chapter]').forEach((chapter, index) => chapter.dataset.active = String(index === active));
    status.textContent = ['01 / Meet your next device', '02 / Know what you’re buying', '03 / Make the upgrade'][active];
    renderer.render(scene, camera);
    if (Math.abs(current - target) > .002) frame = requestAnimationFrame(draw);
  }
  function requestDraw() { if (!frame && renderer && eligible() && visible && !paused && !document.hidden) frame = requestAnimationFrame(draw); }
  function size() {
    if (!renderer) return;
    const rect = viewport.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    renderer.setSize(rect.width, rect.height, false);
    camera.aspect = rect.width / rect.height;
    camera.position.z = Math.max(9.6, 7.6 / camera.aspect);
    camera.lookAt(0, -.05, 0);
    camera.updateProjectionMatrix(); requestDraw();
  }
  function preference() {
    if (!eligible()) {
      cancelAnimationFrame(frame); frame = 0;
      fallback(media.matches ? 'Static view · reduced motion' : 'Real product photography');
    } else if (renderer) { root.dataset.webgl = 'ready'; pause.hidden = false; size(); }
    else start();
  }
  const opts = { signal: controller.signal };
  window.addEventListener('scroll', requestDraw, { passive: true, signal: controller.signal });
  document.addEventListener('visibilitychange', requestDraw, opts);
  media.addEventListener('change', preference, opts); mobile.addEventListener('change', preference, opts);
  pause?.addEventListener('click', () => { paused = !paused; pause.setAttribute('aria-pressed', String(paused)); pause.textContent = paused ? 'Resume scene motion' : 'Pause scene motion'; if (paused) { cancelAnimationFrame(frame); frame = 0; } else requestDraw(); }, opts);
  document.addEventListener('shopify:section:unload', event => { if (event.target.contains(root)) destroy(); }, opts);

  let starting = false;
  async function start() {
    if (starting || !eligible()) { if (!renderer) fallback(media.matches ? 'Static view · reduced motion' : 'Real product photography'); return; }
    starting = true;
    try {
      const THREE = await import(root.dataset.threeModule);
      if (!eligible()) { starting = false; preference(); return; }
      scene = new THREE.Scene();
      camera = new THREE.PerspectiveCamera(32, 1, .1, 40); camera.position.set(0, .12, 9.6); camera.lookAt(0, -.05, 0);
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'low-power' });
      renderer.setPixelRatio(Math.min(devicePixelRatio || 1, 1.5)); renderer.setClearColor(0x000000, 0); renderer.outputColorSpace = THREE.SRGBColorSpace; renderer.toneMapping = THREE.ACESFilmicToneMapping; renderer.toneMappingExposure = 1.05;
      renderer.domElement.setAttribute('aria-hidden', 'true'); renderer.domElement.setAttribute('tabindex', '-1'); viewport.append(renderer.domElement);
      renderer.domElement.addEventListener('webglcontextlost', event => { event.preventDefault(); cancelAnimationFrame(frame); frame = 0; fallback('Real product photography'); }, opts);
      // Original studio light rig: large reflection cards, not downloaded HDR/model assets.
      const studio = new THREE.Scene();
      studio.background = new THREE.Color(0x242b36);
      [[0xffffff, 8, 3, -4, 3, 1.1],[0xdce9ff, 5, 4, 4, 2, .65],[0xffffff, 7, 2, 0, 6, .7]].forEach(([color,w,h,x,y,z]) => {
        const card=new THREE.Mesh(new THREE.PlaneGeometry(w,h),new THREE.MeshBasicMaterial({color,side:THREE.DoubleSide}));
        card.position.set(x,y,z);card.lookAt(0,0,0);studio.add(card);
      });
      const pmrem = new THREE.PMREMGenerator(renderer);
      environmentTarget=pmrem.fromScene(studio,.02);scene.environment=environmentTarget.texture;pmrem.dispose();
      studio.traverse(object=>{object.geometry?.dispose();object.material?.dispose();});
      scene.add(new THREE.HemisphereLight(0xf6f8ff,0x202b3b,1.35));
      [[0xffffff,3.8,-3,4,5],[0xc4d7ff,1.6,4,1,2],[0xffffff,1.4,0,-2,4]].forEach(([color,intensity,x,y,z]) => { const light = new THREE.DirectionalLight(color,intensity);light.position.set(x,y,z);scene.add(light);lights.push(light); });
      const shell=new THREE.MeshPhysicalMaterial({color:0x2d3541,metalness:.35,roughness:.26,clearcoat:.5,clearcoatRoughness:.28,envMapIntensity:.75});
      const trim=new THREE.MeshStandardMaterial({color:0xbec5cf,metalness:.94,roughness:.2,envMapIntensity:1.2});
      const glass=new THREE.MeshPhysicalMaterial({color:0x080d18,metalness:.08,roughness:.15,clearcoat:1,clearcoatRoughness:.1});
      function artwork(kind,back=false) {
        const canvas=document.createElement('canvas');canvas.width=512;canvas.height=kind==='laptop'?384:768;
        const ctx=canvas.getContext('2d'),w=canvas.width,h=canvas.height;
        const gradient=ctx.createLinearGradient(0,0,w,h);gradient.addColorStop(0,back?'#28303b':'#071324');gradient.addColorStop(1,back?'#1b2029':'#244d79');ctx.fillStyle=gradient;ctx.fillRect(0,0,w,h);
        if(!back){
          const glow=ctx.createRadialGradient(w*.77,h*.43,0,w*.77,h*.43,w*.83);glow.addColorStop(0,'#7eafe8');glow.addColorStop(.42,'#365f9a');glow.addColorStop(1,'#102641');ctx.fillStyle=glow;ctx.fillRect(0,0,w,h);
          ctx.save();ctx.translate(w*.7,h*.47);ctx.rotate(-.3);ctx.scale(1,.77);
          for(let n=0;n<6;n++){ctx.beginPath();ctx.arc(0,0,w*(.17+n*.1),-.35,Math.PI*1.65);ctx.strokeStyle='rgba(213,234,255,'+(.62-n*.075)+')';ctx.lineWidth=n===0?5:1.5;ctx.stroke();}ctx.restore();
          const sheen=ctx.createLinearGradient(0,0,w,h);sheen.addColorStop(0,'rgba(255,255,255,.12)');sheen.addColorStop(.45,'rgba(255,255,255,0)');sheen.addColorStop(1,'rgba(0,0,0,.15)');ctx.fillStyle=sheen;ctx.fillRect(0,0,w,h);
          ctx.fillStyle='#e9f3ff';ctx.font='600 32px Arial, sans-serif';ctx.fillText('DIGI',w*.1,h*.13);
          ctx.font='16px Arial, sans-serif';ctx.fillStyle='#cedff6';ctx.fillText('YOUR NEXT CHAPTER',w*.1,h*.91);
        }else{
          ctx.fillStyle='#8f9bab';ctx.font='500 25px Arial, sans-serif';ctx.textAlign='center';ctx.fillText('DIGI',w*.5,h*.5);
        }
        const texture=new THREE.CanvasTexture(canvas);texture.colorSpace=THREE.SRGBColorSpace;texture.anisotropy=Math.min(4,renderer.capabilities.getMaxAnisotropy());textures.push(texture);return texture;
      }
      function roundShape(w,h,r) { const shape=new THREE.Shape(),x=-w/2,y=-h/2;shape.moveTo(x+r,y);shape.lineTo(x+w-r,y);shape.quadraticCurveTo(x+w,y,x+w,y+r);shape.lineTo(x+w,y+h-r);shape.quadraticCurveTo(x+w,y+h,x+w-r,y+h);shape.lineTo(x+r,y+h);shape.quadraticCurveTo(x,y+h,x,y+h-r);shape.lineTo(x,y+r);shape.quadraticCurveTo(x,y,x+r,y);return shape; }
      function panel(w,h,d,r,material) { const geometry=new THREE.ExtrudeGeometry(roundShape(w,h,r),{depth:d,bevelEnabled:true,bevelSegments:3,steps:1,bevelSize:.012,bevelThickness:.012,curveSegments:16});geometry.translate(0,0,-d/2);return new THREE.Mesh(geometry,material); }
      function surface(w,h,r,texture,back=false) {
        const geometry=new THREE.ShapeGeometry(roundShape(w,h,r),16),position=geometry.attributes.position,uv=geometry.attributes.uv;
        for(let i=0;i<position.count;i++)uv.setXY(i,position.getX(i)/w+.5,position.getY(i)/h+.5);
        const material=back?new THREE.MeshStandardMaterial({map:texture,metalness:.25,roughness:.3}):new THREE.MeshBasicMaterial({map:texture,toneMapped:false});
        return new THREE.Mesh(geometry,material);
      }
      function device(w,h,kind='phone') {
        const group=new THREE.Group();group.add(panel(w,h,.085,.11,trim));
        const back=surface(w-.035,h-.035,.095,artwork(kind,true),true);back.position.z=-.055;back.rotation.y=Math.PI;group.add(back);
        const face=panel(w-.035,h-.035,.012,.095,glass);face.position.z=.052;group.add(face);
        const display=surface(w-.115,h-.14,.075,artwork(kind));display.position.z=.077;group.add(display);
        return group;
      }
      geometryWorld=new THREE.Group();scene.add(geometryWorld);
      phone=device(1.15,2.27);geometryWorld.add(phone);
      tablet=device(1.74,2.19,'tablet');tablet.scale.setScalar(.89);geometryWorld.add(tablet);
      laptop=new THREE.Group();const base=panel(2.27,1.45,.095,.09,trim);base.rotation.x=-Math.PI/2;laptop.add(base);const keyboard=new THREE.Mesh(new THREE.BoxGeometry(1.77,.025,.55),glass);keyboard.position.set(0,.07,-.23);laptop.add(keyboard);const trackpad=new THREE.Mesh(new THREE.BoxGeometry(.65,.014,.35),shell);trackpad.position.set(0,.072,.35);laptop.add(trackpad);const hinge=new THREE.Group();hinge.position.set(0,0,-.69);const lid=device(2.24,1.44,'laptop');lid.position.set(0,.76,0);hinge.add(lid);laptop.add(hinge);laptop.userData.lid=hinge;geometryWorld.add(laptop);
      orbit=new THREE.Group();const ringMat=new THREE.MeshBasicMaterial({color:0x6b95d5,transparent:true,opacity:.12});[2.6].forEach(radius=>{const ring=new THREE.Mesh(new THREE.TorusGeometry(radius,.008,6,100),ringMat);ring.rotation.x=-1.25;ring.position.y=-1.12;orbit.add(ring);});scene.add(orbit);
      resizeObserver=new ResizeObserver(size);resizeObserver.observe(viewport);root.dataset.webgl='ready';pause.hidden=false;starting=false;size();requestDraw();
    } catch(error) { starting=false; renderer?.dispose(); renderer?.domElement.remove(); renderer=null; fallback('Real product photography'); root.dataset.sceneError='unavailable'; }
  }
  observer=new IntersectionObserver(entries=>{visible=entries[0].isIntersecting;if(visible){if(renderer)requestDraw();else start();}else{cancelAnimationFrame(frame);frame=0;}},{rootMargin:'150px',threshold:0});observer.observe(root);
  preference();
}
function scan(parent=document) { parent.querySelectorAll('[data-digi-upgrade-scene]').forEach(mount); }
scan(); document.addEventListener('shopify:section:load',event=>scan(event.target));
